import axios from 'axios'
import { ElMessage } from 'element-plus'
import { forceAuthRelogin, isMobileWorkbenchRoutePath, markUserActive } from '@/utils/auth'
import { afterRequestGuard, beforeRequestGuard } from '@/utils/requestGuard'

/** 同类网络提示防抖，避免超时连弹多条 */
let lastNetworkToastAt = 0
let lastNetworkToastKey = ''

/** 请求被主动取消（重复 GET / 切页 abort）——可忽略，不应红字打扰 */
export function isRequestCanceledError(error) {
  if (!error) return false
  if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError') {
    return true
  }
  if (error.silentToast === true || error.isCanceled === true) return true
  // Axios CanceledError 文案多为 "canceled"；兼容带前后缀的 abort 提示
  return /canceled|cancelled|aborted/i.test(String(error.message || ''))
}

/**
 * 业务 catch 里统一用：取消类请求不弹窗。
 * @returns {boolean} true 表示已处理（含已忽略）
 */
export function toastApiError(error, fallback = '操作失败') {
  if (isRequestCanceledError(error)) return true
  const raw = error?.response?.data?.message || error?.message
  if (/^(canceled|cancelled|aborted)$/i.test(String(raw || '').trim())) return true
  const msg = raw || fallback
  if (!msg) return true
  ElMessage.error({ message: String(msg), showClose: true, duration: 8000 })
  return true
}

function toastNetworkOnce(key, message, type = 'error') {
  const now = Date.now()
  if (key === lastNetworkToastKey && now - lastNetworkToastAt < 4000) {
    return
  }
  lastNetworkToastKey = key
  lastNetworkToastAt = now
  if (type === 'error' && /^(canceled|cancelled|aborted)$/i.test(String(message || '').trim())) {
    return
  }
  const fn = type === 'warning' ? ElMessage.warning : ElMessage.error
  fn({
    message,
    showClose: true,
    duration: 8000
  })
}

// 创建axios实例
// 使用相对路径，自动适配动态IP地址
// 如果设置了环境变量 VUE_APP_API_BASE_URL，则使用环境变量
// 否则使用相对路径（前后端在同一服务器上）
// 如果需要在开发环境使用特定地址，可以通过 .env 文件设置
const getApiBaseUrl = () => {
  // 优先使用环境变量
  if (process.env.VUE_APP_API_BASE_URL) {
    return process.env.VUE_APP_API_BASE_URL
  }
  
  // 生产环境：使用相对路径，自动适配当前访问的IP和端口
  // 这样无论IP怎么变化，都能正常工作
  if (process.env.NODE_ENV === 'production') {
    // 使用相对路径，axios会自动使用当前页面的origin
    return ''
  }
  
  // 开发环境：可以使用相对路径，或者从window.location获取
  // 如果前后端在同一服务器，使用相对路径即可
  return ''
}

const request = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 20000
})

/** 非鉴权探针的 401 提示节流，避免轮询接口刷屏 */
let lastSoft401WarnAt = 0
/** 移动端 401 二次 verify 去重，避免并发请求重复弹登出 */
let mobileAuthVerifyPromise = null

function shouldForceLogoutOn401(config) {
  if (config?.skipAuthRedirect) return false
  if (config?.authCritical) return true
  const url = String(config?.url || '')
  // 仅鉴权接口 401 立即登出；业务接口 401 在移动端先二次 verify，避免误踢
  return /\/api\/auth\/(login|logout|verify)\b/.test(url)
}

/** 后端已判定 token/会话不可用（非临时权限问题），应清 token 并回登录页 */
function isDefinitiveSessionFailure(error) {
  const msg = String(error?.response?.data?.message || '')
  return /登录会话已失效|当前登录会话已失效|Token expired|Invalid token|User not found|Valid token is required|Access denied/i.test(
    msg
  )
}

async function confirmMobileSessionBeforeLogout(failedMessage) {
  if (mobileAuthVerifyPromise) {
    try {
      return await mobileAuthVerifyPromise
    } catch {
      return false
    }
  }
  mobileAuthVerifyPromise = request({
    url: '/api/auth/verify',
    method: 'post',
    timeout: 12000,
    skipAuthRedirect: true,
    authCritical: true
  })
    .then((res) => !!(res?.success && res.user))
    .catch(() => false)
    .finally(() => {
      mobileAuthVerifyPromise = null
    })
  const ok = await mobileAuthVerifyPromise
  if (!ok) {
    forceAuthRelogin(failedMessage || '登录已失效，请重新登录')
  }
  return ok
}

// 请求拦截器（async：防护超额时排队等待，不直接拒绝）
request.interceptors.request.use(
  async (config) => {
    const guardErr = await beforeRequestGuard(config)
    if (guardErr) {
      return Promise.reject(guardErr)
    }

    // 从localStorage获取token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      markUserActive()
    }

    // 移动工作台 /m 下请求 API 时带上标记，后端可对齐校验 mobile.*（桌面不传此头，行为不变）
    try {
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/m')) {
        config.headers['X-ERP-Mobile'] = '1'
      }
    } catch {
      /* ignore */
    }

    // 移动端盘点盖章 POST 在弱网下易被默认 20s 截断；与 inventoryStockTake.js 取较大值
    const u = config.url || ''
    if (u.includes('/inventory-stock-take/sessions') && String(config.method || '').toLowerCase() === 'post') {
      const cur = Number(config.timeout)
      config.timeout = Math.max(Number.isFinite(cur) ? cur : 0, 120000)
    }
    
    // 特殊处理：如果是期初入库请求，确保transaction_type_id和material_batch_id存在
    if (config.url === '/api/inventory-transactions' && config.method === 'post' && config.data) {
      // 检查是否是期初入库（通过is_initial_stock标记）
      if (config.data.is_initial_stock === true) {
        console.log('请求拦截器：检测到期初入库请求')
        console.log('原始数据中的transaction_type_id:', config.data.transaction_type_id, '类型:', typeof config.data.transaction_type_id)
        console.log('原始数据的所有键:', Object.keys(config.data))
        
        // 如果transaction_type_id不存在或无效，直接设置（使用硬编码值6作为备用）
        if (!config.data.transaction_type_id || 
            config.data.transaction_type_id === null || 
            config.data.transaction_type_id === undefined ||
            config.data.transaction_type_id === '' ||
            Number(config.data.transaction_type_id) <= 0) {
          console.warn('请求拦截器：期初入库请求缺少transaction_type_id，使用备用值6')
          config.data.transaction_type_id = 6  // 期初入库的ID是6
          console.log('请求拦截器：已设置transaction_type_id为:', config.data.transaction_type_id)
        } else {
          // 确保transaction_type_id是数字类型
          const typeId = Number(config.data.transaction_type_id)
          if (!isNaN(typeId) && typeId > 0) {
            config.data.transaction_type_id = typeId
            console.log('请求拦截器：确保transaction_type_id是数字类型:', typeId)
          } else {
            console.warn('请求拦截器：transaction_type_id转换失败，使用备用值6')
            config.data.transaction_type_id = 6
          }
        }
        
        // 确保material_batch_id存在（后端要求必须有）
        // 如果batch_id存在，设置material_batch_id
        if (config.data.batch_id !== null && config.data.batch_id !== undefined) {
          config.data.material_batch_id = config.data.batch_id
          console.log('请求拦截器：已设置material_batch_id为:', config.data.material_batch_id, '(从batch_id获取)')
        } else if (config.data.material_batch_id !== null && config.data.material_batch_id !== undefined) {
          // 如果material_batch_id已经存在，确保它是数字类型
          const batchId = Number(config.data.material_batch_id)
          if (!isNaN(batchId) && batchId > 0) {
            config.data.material_batch_id = batchId
            console.log('请求拦截器：已确保material_batch_id为数字类型:', config.data.material_batch_id)
          }
        } else {
          // 如果都没有，尝试从system_batch_code获取batch_id
          // 注意：这需要调用API，但为了简化，我们可以尝试从localStorage或其他地方获取
          // 或者，我们可以要求用户必须选择料号
          console.error('请求拦截器：期初入库请求缺少material_batch_id和batch_id')
          console.error('原始数据的所有键:', Object.keys(config.data))
          console.error('原始数据:', JSON.stringify(config.data, null, 2))
          
          // 尝试从system_batch_code获取batch_id（如果存在）
          // 但这需要调用API，所以这里我们无法自动修复
          // 只能提示用户必须选择料号
          if (config.data.system_batch_code) {
            console.warn('检测到system_batch_code:', config.data.system_batch_code, '但无法自动获取batch_id')
            console.warn('请确保在期初入库时选择了料号')
          }
        }
        
        // 最终验证：确保transaction_type_id在发送前一定存在
        if (!config.data.transaction_type_id || config.data.transaction_type_id <= 0) {
          console.error('请求拦截器：最终验证失败，transaction_type_id仍然无效！强制设置为6')
          config.data.transaction_type_id = 6
        } else {
          console.log('请求拦截器：最终验证通过，transaction_type_id:', config.data.transaction_type_id)
        }
        
        // 最终验证：确保material_batch_id存在
        if (!config.data.material_batch_id) {
          console.error('请求拦截器：最终验证失败，material_batch_id仍然无效！')
          console.error('最终数据:', JSON.stringify(config.data, null, 2))
          console.error('最终数据的所有键:', Object.keys(config.data))
          
          // 如果system_batch_code存在，提示用户必须选择料号
          if (config.data.system_batch_code) {
            console.error('检测到system_batch_code:', config.data.system_batch_code, '但无法自动获取batch_id')
            console.error('请确保在期初入库时选择了料号，batch_id是必需的')
          }
          
          // 注意：这里我们无法自动修复，因为需要从数据库查询batch_id
          // 但是我们可以尝试从localStorage或其他地方获取（如果有的话）
          // 或者，我们可以要求用户必须选择料号
        } else {
          console.log('请求拦截器：最终验证通过，material_batch_id:', config.data.material_batch_id)
        }
      }
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    afterRequestGuard(response.config, null)
    // 对于 blob 类型的响应，直接返回 response.data（已经是 Blob 对象）
    // 对于其他类型的响应，返回 response.data
    if (response.config.responseType === 'blob') {
      return response.data
    }
    return response.data
  },
  (error) => {
    afterRequestGuard(error?.config, error)
    // 对响应错误做点什么
    if (isRequestCanceledError(error)) {
      try {
        error.isCanceled = true
        error.silentToast = true
      } catch {
        /* ignore non-extensible */
      }
      return Promise.reject(error)
    }
    if (error?.code === 'ERR_TOO_MANY_REQUESTS' || error?.code === 'ERR_REQUEST_COOLDOWN') {
      toastNetworkOnce(error.code, error.message || '请稍后再试，勿连续刷新', 'warning')
      return Promise.reject(error)
    }
    if (error.response?.status === 401) {
      const cfg = error.config || {}
      const msg =
        error.response?.data?.message ||
        (isMobileWorkbenchRoutePath()
          ? '登录已失效，请重新登录'
          : '登录已过期，请重新登录')
      const sessionDead = isDefinitiveSessionFailure(error)
      if (shouldForceLogoutOn401(cfg) || sessionDead) {
        forceAuthRelogin(msg)
      } else if (isMobileWorkbenchRoutePath() && !cfg._mobileAuthRechecked) {
        cfg._mobileAuthRechecked = true
        return confirmMobileSessionBeforeLogout(msg).then((sessionOk) => {
          if (!sessionOk) {
            return Promise.reject(error)
          }
          const now = Date.now()
          if (now - lastSoft401WarnAt > 12_000) {
            lastSoft401WarnAt = now
            ElMessage.warning(
              error.response?.data?.message ||
                '该请求未授权，登录仍有效；若功能异常请稍后重试或重新登录'
            )
          }
          return Promise.reject(error)
        })
      } else if (!cfg.skipAuthRedirect) {
        const now = Date.now()
        if (now - lastSoft401WarnAt > 12_000) {
          lastSoft401WarnAt = now
          ElMessage.warning(
            error.response?.data?.message || '部分请求未授权，可继续使用；若功能异常请重新登录'
          )
        }
      }
    } else if (error.response?.status === 403) {
      ElMessage.error({
        message: error.response?.data?.message || '无权限执行该操作',
        showClose: true,
        duration: 8000
      })
    } else if (error.response?.status === 400) {
      // 400错误由调用方自己处理，不在这里自动显示消息
      // 这样可以让调用方显示更详细的错误信息（如导入错误列表、相似颜色检查等）
      // 特别是不显示消息，让调用方处理 requires_confirmation 和 blocked 的情况
    } else if (error.config?.skipErrorToast) {
      /* 调用方自行提示（如外发快捷采购拉默认胚布，失败时已用配料预填） */
    } else if (error.response?.data?.message) {
      // 只有非400错误才自动显示消息
      ElMessage.error({
        message: error.response.data.message,
        showClose: true,
        duration: 8000
      })
    } else if (error.response?.status) {
      // 502/504 等常为 HTML 正文，无 message 字段
      const st = error.response.status
      const stText = error.response.statusText
      toastNetworkOnce(
        `http-${st}`,
        stText ? `服务异常（${st} ${stText}），请稍后重试` : `服务异常（${st}），请稍后重试`
      )
    } else if (
      error.response?.status === 504 ||
      error.response?.data?.code === 'db_query_timeout'
    ) {
      toastNetworkOnce(
        'db-timeout',
        error.response?.data?.message || '查询超时已中断，请缩小范围后重试，勿连续刷新'
      )
    } else if (error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout')) {
      toastNetworkOnce('timeout', '请求超时，请稍后重试，勿连续刷新')
    } else {
      toastNetworkOnce('network', '网络错误，请稍后重试')
    }
    // 确保返回完整的错误对象，包含 response.data
    return Promise.reject(error)
  }
)

export default request